import { useCallback, useEffect, useRef, useState } from 'react';
import APIAgent from '../../../agents/apiAgent';

export function usePropertyBrowse({ userId, pageSize = 12 }) {
  const [houses, setHouses] = useState([]);
  const [page, setPage] = useState(1);
  const [search, setSearchState] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [favorites, setFavorites] = useState([]);

  const debounceRef = useRef(null);
  // keyset pagination cursor map: page number → id cursor (null = first page)
  const cursorMapRef = useRef({ 1: null });

  const setSearch = useCallback((value) => {
    setSearchState(value);
    setPage(1);
    cursorMapRef.current = { 1: null }; // reset cursor map when search term changes
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setDebouncedSearch(value);
    }, 300);
  }, []);

  useEffect(() => () => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
  }, []);

  const fetchAllHousesForSearch = useCallback(async () => {
    const pageLimit = 200;
    const maxBatches = 500;
    let cursor = null;
    let batchCount = 0;
    const allHouses = [];

    while (batchCount < maxBatches) {
      const result = await APIAgent.getHouses(cursor, pageLimit, false);
      const batch = result?.houses || [];
      allHouses.push(...batch);

      if (!batch.length || result?.next_cursor == null) break;

      cursor = result.next_cursor;
      batchCount += 1;
    }

    return allHouses;
  }, []);

  const fetchPage = useCallback(async (pageNum, searchQuery) => {
    setLoading(true);
    try {
      const skip = (pageNum - 1) * pageSize;
      let result;
      if (searchQuery && searchQuery.trim()) {
        result = await APIAgent.getHousesByCriteria(
          { city: searchQuery.trim() },
          skip,
          pageSize,
          pageNum === 1
        );
        if (result.total_count === 0) {
          const allHouses = await fetchAllHousesForSearch();
          const filtered = allHouses.filter(h =>
            `${h.address} ${h.city} ${h.state} ${h.zip_code}`
              .toLowerCase()
              .includes(searchQuery.trim().toLowerCase())
          );
          result = {
            houses: filtered.slice(skip, skip + pageSize),
            total_count: filtered.length,
          };
        }
      } else {
        const cursor = cursorMapRef.current[pageNum] ?? null;
        result = await APIAgent.getHouses(cursor, pageSize, pageNum === 1);
        if (result.next_cursor != null) {
          cursorMapRef.current[pageNum + 1] = result.next_cursor;
        }
      }
      setHouses(result.houses || []);
      if (typeof result.total_count === 'number') {
        setTotalCount(result.total_count);
      }
      setError(null);
    } catch (err) {
      setError(err.message);
      console.error('Error fetching browse houses:', err);
    } finally {
      setLoading(false);
    }
  }, [fetchAllHousesForSearch, pageSize]);

  const fetchFavorites = useCallback(async () => {
    if (!userId) return;
    try {
      const favs = await APIAgent.request(`/api/buyers/${userId}/favorites`);
      if (!favs.length) { setFavorites([]); return; }
      const housePromises = favs.map(fav =>
        APIAgent.request(`/api/houses/${fav.house_id}`).catch(() => null)
      );
      const houseList = (await Promise.all(housePromises)).filter(Boolean);
      setFavorites(houseList);
    } catch (err) {
      console.error('Error fetching favorite houses:', err.message);
    }
  }, [userId]);

  useEffect(() => {
    fetchPage(page, debouncedSearch);
  }, [page, debouncedSearch, fetchPage]);

  const refetch = useCallback(() => {
    fetchPage(page, debouncedSearch);
  }, [fetchPage, page, debouncedSearch]);

  return {
    houses,
    page,
    search,
    totalCount,
    loading,
    error,
    favorites,
    setPage,
    setSearch,
    fetchPage,
    fetchFavorites,
    refetch,
  };
}
